import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import './WebhooksModal.css'

export interface AppSecretEntry {
  id: number
  name: string
  help_string: string
  created_at?: string
}

interface AppSecretsModalProps {
  entries: AppSecretEntry[]
  onClose: () => void
}

function AppSecretsModal({ entries, onClose }: AppSecretsModalProps) {
  const modalRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement
    modalRef.current?.focus()

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])'
        )
        if (focusable.length === 0) return
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      previousFocusRef.current?.focus()
    }
  }, [onClose])

  return (
    <div className="webhooks-modal-overlay" data-testid="app-secrets-modal-overlay" onClick={onClose}>
      <div
        className="webhooks-modal"
        data-testid="app-secrets-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-secrets-modal-title"
        ref={modalRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="webhooks-modal__header">
          <div>
            <h2 className="webhooks-modal__title" id="app-secrets-modal-title" data-testid="app-secrets-modal-title">Additional Secrets</h2>
            <p className="webhooks-modal__subtitle">
              Extra secret-allowlist verbs configured for this app. Containers this app spawns
              may run these commands in addition to the platform defaults.
            </p>
          </div>
          <button
            className="webhooks-modal__close"
            onClick={onClose}
            aria-label="Close dialog"
            data-testid="app-secrets-modal-close"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        <div className="webhooks-modal__body">
          {entries.length === 0 ? (
            <p className="webhooks-modal__description" data-testid="app-secrets-empty">
              No additional secrets are configured for this app.
            </p>
          ) : (
            entries.map((entry) => (
              <div
                className="webhooks-modal__group"
                data-testid="app-secrets-entry"
                key={entry.id}
              >
                <h3 className="webhooks-modal__group-title" data-testid="app-secrets-entry-name">
                  <code>{entry.name}</code>
                </h3>
                <p className="webhooks-modal__description" data-testid="app-secrets-entry-help">
                  {entry.help_string}
                </p>
              </div>
            ))
          )}
        </div>

        <div className="webhooks-modal__actions">
          <button
            className="webhooks-modal__btn webhooks-modal__btn--close"
            onClick={onClose}
            data-testid="app-secrets-modal-done"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  )
}

export default AppSecretsModal
